// apps/client/src/hooks/reports/useAwaitingPayment.ts
// "Settled, awaiting payment" on the Approved vs Settled v2 page.
//
// One callable, awaitingPaymentAction, discriminated by `action`
// (list / detail / setStatus / addNote / searchLoans; contract in
// @valutten/shared-schemas approvedVsSettledV2.ts). Confirming or dismissing a
// possible lender payment goes through the deal-to-loan link's own callable,
// decideDealLoanLink. There is deliberately no way to mark a deal Paid here:
// Paid is only ever read from a statement match.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type {
  AwaitingPaymentActionRequest,
  AwaitingPaymentDetailResponse,
  AwaitingPaymentListResponse,
  AwaitingPaymentSearchLoansResponse,
  AwaitingPaymentSetStatus,
  AwaitingPaymentWriteResponse,
  NotExpectedReason,
} from '@valutten/shared-schemas';
import { functions } from '../../lib/firebase';

export interface AwaitingPaymentFilters {
  companyId: string | null | undefined;
  startMonth: string;
  endMonth: string;
  officeId?: string;
  brokerId?: string;
  templateId?: string;
  entityCode?: string;
}

/** Drop undefined keys so optional filters are absent, not null, on the wire. */
function compact<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

function callAwaiting<Res>(req: AwaitingPaymentActionRequest): Promise<Res> {
  const callable = httpsCallable<AwaitingPaymentActionRequest, Res>(functions, 'awaitingPaymentAction');
  return callable(compact(req)).then((r) => r.data);
}

export function useAwaitingPaymentList(filters: AwaitingPaymentFilters) {
  const { companyId, startMonth, endMonth, officeId, brokerId, templateId, entityCode } = filters;
  const [data, setData] = useState<AwaitingPaymentListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!companyId || !startMonth || !endMonth) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    callAwaiting<AwaitingPaymentListResponse>({
      action: 'list',
      companyId,
      startMonth,
      endMonth,
      officeId,
      brokerId,
      templateId,
      entityCode,
    })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useAwaitingPaymentList] Callable failed:', err);
        setError(err?.message || 'Failed to load deals awaiting payment');
        setData(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, startMonth, endMonth, officeId, brokerId, templateId, entityCode, tick]);

  return { data, loading, error, refetch };
}

export function useAwaitingPaymentDetail(companyId: string | null | undefined, dealId: string | null) {
  const [data, setData] = useState<AwaitingPaymentDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!companyId || !dealId) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    callAwaiting<AwaitingPaymentDetailResponse>({ action: 'detail', companyId, dealId })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useAwaitingPaymentDetail] Callable failed:', err);
        setError(err?.message || 'Failed to load this deal');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, dealId, tick]);

  // A different deal must never flash the previous deal's activity.
  useEffect(() => {
    setData(null);
  }, [dealId]);

  return { data, loading, error, refetch };
}

export interface SetAwaitingStatusInput {
  companyId: string;
  dealId: string;
  status: AwaitingPaymentSetStatus;
  reason?: NotExpectedReason;
  note?: string;
  followUpDate?: string;
  /** The row's revision the person was looking at (0 when nothing is stored yet). */
  expectedRevision: number;
}

export function setAwaitingPaymentStatus(input: SetAwaitingStatusInput): Promise<AwaitingPaymentWriteResponse> {
  return callAwaiting<AwaitingPaymentWriteResponse>({ action: 'setStatus', ...input });
}

export function addAwaitingPaymentNote(input: { companyId: string; dealId: string; note: string }): Promise<AwaitingPaymentWriteResponse> {
  return callAwaiting<AwaitingPaymentWriteResponse>({ action: 'addNote', ...input });
}

/**
 * "Find the payment": the caller's statement loans for one deal. Empty query =
 * loans settled near the deal at a similar amount; otherwise client name words
 * or a loan number. Linking a result uses decideAwaitingPayment (confirm).
 */
export function searchAwaitingPaymentLoans(input: {
  companyId: string;
  dealId: string;
  query?: string;
}): Promise<AwaitingPaymentSearchLoansResponse> {
  return callAwaiting<AwaitingPaymentSearchLoansResponse>({ action: 'searchLoans', ...input });
}

/** Confirm or dismiss the possible lender payment shown as "Payment to confirm". */
export function decideAwaitingPayment(input: {
  companyId: string;
  dealId: string;
  action: 'confirm' | 'dismiss';
  loanIds: string[];
}): Promise<unknown> {
  const callable = httpsCallable<typeof input, unknown>(functions, 'decideDealLoanLink');
  return callable(input).then((r) => r.data);
}

/** The write lost a race with another person's change (stale expectedRevision). */
export function isStaleRevisionError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return code === 'functions/failed-precondition' || code === 'failed-precondition';
}
